import { Suspense, ReactNode } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ROUTES } from "@/domain/constants/routes";

// 組件導入
import { SmartRouter } from "./SmartRouter";
import { ProtectedRoute } from "./ProtectedRoute";
import {
  HomePage,
  // ChatPage,
  LoginPage,
  ProfilePage,
  TermsPage,
  PrivacyPage,
  NotFoundPage,
  PermissionDeniedPage,
  ConversationPage,
} from "@/ui/pages";

/**
 * 建立受保護路由
 * @param path 路徑
 * @param element 要渲染的組件
 * @returns 路由組件
 */
const createProtectedRoute = (path: string, element: ReactNode) => (
  <Route path={path} element={<ProtectedRoute>{element}</ProtectedRoute>} />
);

/**
 * 建立多個使用相同組件的受保護路由
 * @param paths 路徑列表
 * @param element 要渲染的組件
 * @returns 路由組件數組
 */
const createProtectedRoutes = (paths: string[], element: ReactNode) =>
  paths.map((path) => createProtectedRoute(path, element));

/**
 * 應用路由組件
 * 集中管理所有路由配置
 */
export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-pulse text-gray-700 dark:text-gray-300">
              載入中...
            </div>
          </div>
        }
      >
        <SmartRouter>
          <Routes>
            {/* 公開路由 */}
            <Route path={ROUTES.HOME} element={<HomePage />} />
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.TERMS} element={<TermsPage />} />
            <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />

            {/* 首頁相關路由 - 使用 ProfilePage */}
            {createProtectedRoutes(
              [ROUTES.ACTIVITY, ROUTES.NOTIFICATIONS],
              <ProfilePage />,
            )}

            {/* 會話相關路由 - 使用 ProfilePage */}
            {createProtectedRoutes([ROUTES.CONVERSATION], <ConversationPage />)}

            {/* 網站相關路由 - 使用 ProfilePage */}
            {createProtectedRoutes(
              [
                ROUTES.WEBSITE,
                ROUTES.WEBSITE_PAGES,
                ROUTES.WEBSITE_TEMPLATES,
                ROUTES.WEBSITE_DOMAINS,
              ],
              <ProfilePage />,
            )}

            {/* SEO 相關路由 - 使用 ProfilePage */}
            {createProtectedRoutes(
              [ROUTES.SEO, ROUTES.SEO_KEYWORDS, ROUTES.SEO_PERFORMANCE],
              <ProfilePage />,
            )}

            {/* 資源相關路由 - 使用 ProfilePage */}
            {createProtectedRoutes(
              [
                ROUTES.RESOURCES,
                ROUTES.RESOURCES_MEDIA,
                ROUTES.RESOURCES_DOCUMENTS,
              ],
              <ProfilePage />,
            )}
            {/* 資源上傳路由 */}
            {createProtectedRoute(ROUTES.RESOURCES_UPLOADS, <ProfilePage />)}

            {/* 分析相關路由 - 使用 ProfilePage */}
            {createProtectedRoutes(
              [
                ROUTES.ANALYTICS,
                ROUTES.ANALYTICS_TRAFFIC,
                ROUTES.ANALYTICS_CONVERSION,
              ],
              <ProfilePage />,
            )}

            {/* 行銷相關路由 - 使用 ProfilePage */}
            {createProtectedRoutes(
              [ROUTES.MARKETING, ROUTES.MARKETING_CAMPAIGNS],
              <ProfilePage />,
            )}
            {/* 行銷其他路由 */}
            {createProtectedRoutes(
              [ROUTES.MARKETING_EMAIL, ROUTES.MARKETING_SOCIAL],
              <ProfilePage />,
            )}

            {/* 專案相關路由 - 使用 ProfilePage */}
            {createProtectedRoutes(
              [
                ROUTES.PROJECTS,
                ROUTES.PROJECTS_LIST,
                ROUTES.PROJECTS_TEMPLATES,
              ],
              <ProfilePage />,
            )}

            {/* 設定路由 */}
            {createProtectedRoute(ROUTES.SETTINGS, <ProfilePage />)}
            {/* 設定相關路由 - 使用 ProfilePage */}
            {createProtectedRoutes(
              [
                ROUTES.SETTINGS_ACCOUNT,
                ROUTES.SETTINGS_BILLING,
                ROUTES.SETTINGS_TEAM,
              ],
              <ProfilePage />,
            )}

            {/* 個人資料路由 */}
            {createProtectedRoute(ROUTES.PROFILE, <ProfilePage />)}

            {/* 聊天路由 - 使用特定組件 */}
            {/* {createProtectedRoute(ROUTES.CHAT_BUILDING, <ChatPage />)} */}

            {/* 404 頁面路由 */}
            <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />

            {/* 403 權限拒絕頁面路由 */}
            <Route
              path={ROUTES.PERMISSION_DENIED}
              element={<PermissionDeniedPage />}
            />

            {/* 捕獲所有未匹配的路由，導向 404 頁面 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </SmartRouter>
      </Suspense>
    </BrowserRouter>
  );
}
